import { Getter, Setter } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { cloneDeep } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { fetchPersonEmails } from '../queries/MaggieCompanyQueries';
import { peopleByCompanyStateJ, peopleByCompanyStateLoadableJ } from '../state/PeopleByCompanyStateJ';
import { personState } from '../state/PersonStateJ';
import { IPersonDataModel, IPersonEmailsDataModel } from '../../data-models/person.data-model';

const fetchedPersonEmails = new Set<number>();

/**
 * Fetches person emails from a secondary source and merges them into person
 * state.
 *
 * Pass the **company** id whose People list should be patched in-place when
 * the secondary email arrives. Omitting `companyId` (or passing `undefined`)
 * skips the company-scoped patch — `personState` is still updated. Do not
 * pass the person id here: the read-through `peopleByCompanyStateLoadableJ`
 * would treat it as a company id and trigger a spurious
 * `fetchCompanyPeople(personId)` request.
 */
export function usePersonEmails(companyId?: number) {
  const [loading, setLoading] = useState(false);

  const getAndSetPersonEmails = useAtomCallback(
    useCallback(
      async (get: Getter, set: Setter, personId: number) => {
        if (fetchedPersonEmails.has(personId)) {
          return;
        }
        setLoading(true);
        let personEmails: IPersonEmailsDataModel | null = null;
        try {
          personEmails = await fetchPersonEmails(personId);
          if (companyId) {
            // Only patch the `peopleByCompanyStateJ` atom when it has already
            // resolved. Reading the loadable variant avoids triggering an
            // out-of-band fetch if no one has subscribed yet, and lets us
            // pass a plain array to `set` (the atom's value type includes
            // the in-flight Promise, so a functional updater would receive
            // `T | Promise<T>`).
            const loadableValue = get(peopleByCompanyStateLoadableJ(companyId));
            if (loadableValue.state === 'hasData' && loadableValue.data) {
              const updated = loadableValue.data.map((person) => {
                if (person.id !== personId) {
                  return person;
                }
                return cloneDeep({ ...person, ...personEmails });
              });
              set(peopleByCompanyStateJ(companyId), updated);
            }
          }

          const currentPerson = await get(personState(personId));
          if (currentPerson) {
            set(personState(personId), cloneDeep({ ...currentPerson, ...personEmails }));
          }
          return personEmails;
        } catch (err) {
          console.error(err);
        } finally {
          setLoading(false);
          fetchedPersonEmails.add(personId);
        }
      },
      [companyId]
    )
  );

  return { getAndSetPersonEmails, loading };
}

export function useFetchAndUpdatePersonEmails(
  person: Pick<IPersonDataModel, 'primaryEmail' | 'id'>,
  companyId: number
) {
  const { primaryEmail, id } = person ?? {};
  const { getAndSetPersonEmails, loading } = usePersonEmails(companyId);

  useEffect(() => {
    if (loading || (primaryEmail != null && primaryEmail !== 'email_not_unlocked@domain.com')) {
      return;
    }
    getAndSetPersonEmails(id);
  }, [loading, primaryEmail, id, getAndSetPersonEmails]);

  return { loading };
}
